/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import CheckBox from '../../../../atoms/Checkbox/Checkbox';
import { PaymentMethod, PriceCountry } from '../../../../../constants/merchantPriceCatalog';
import { SalesSegment } from '../../../../../types/onboarding/OnboardStore';
import {
    cellKey,
    FeeField,
    feeFields,
    feeRule,
    isCellChanged,
    PriceCell,
    PriceFormValues,
    ProductRow,
    ValidityField,
} from '../../../../../services/helpers/merchantPrices';
import PaymentMethodCodes, { hasFee } from './PaymentMethodCodes';
import { CellInput, Dash, RemoveCodeButton, Td, ValidityFields, ValidityInput } from './styles';

interface PaymentMethodEditRowProps {
    method: PaymentMethod;
    country: PriceCountry;
    segment: SalesSegment;
    /** The loaded values, to tell an unsaved change from what is stored. */
    defaults: PriceFormValues;
}

const percentFields: FeeField[] = ['serviceFee', 'recurringServiceFee'];

const validityFieldLabels: Record<ValidityField, string> = {
    validFrom: 'Valid from',
    validUntil: 'Valid to',
};

/**
 * One payment method in one country and segment as react-hook-form fields: its active checkbox, the fees set on all of its
 * product codes, and the codes themselves as a field array, so removing and redating a code is form state like the fees.
 * Codes are added by the form under the table, which writes to the same field array.
 */
const PaymentMethodEditRow: React.FC<PaymentMethodEditRowProps> = ({
    method,
    country,
    segment,
    defaults,
}: PaymentMethodEditRowProps) => {
    const id = `${method.id}-${country.code}-${segment}`;
    const key = cellKey(method.id, country.code, segment);
    const { control, register, formState, setValue } = useFormContext<PriceFormValues>();
    const { fields, remove } = useFieldArray({ control, name: `products.${key}` });
    const active = useWatch<PriceFormValues>({ name: `active.${key}` }) === true;
    const fees = useWatch<PriceFormValues>({ name: `fees.${key}` }) as PriceCell | undefined;
    // Watched rather than taken from the field array, so a redated campaign code counts as a change too.
    const rows = useWatch<PriceFormValues>({ name: `products.${key}` }) as ProductRow[] | undefined;

    const changed = isCellChanged(defaults, key, {
        active,
        fees: fees ?? defaults.fees[key],
        products: rows ?? [],
    });

    const removeCode = (index: number) => {
        remove(index);

        // A method without any code left cannot be priced here, so it is deactivated in this market and segment.
        if (fields.length === 1) setValue(`active.${key}`, false, { shouldDirty: true });
    };

    const renderFee = (field: FeeField) => {
        if (!hasFee(method, field)) return <Dash>–</Dash>;

        const error = formState.errors.fees?.[key]?.[field]?.message;
        const { ref, ...registered } = register(`fees.${key}.${field}`, {
            validate: feeRule(field),
        });

        return (
            <CellInput
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                max={percentFields.includes(field) ? '100' : undefined}
                disabled={!active}
                aria-invalid={error ? true : undefined}
                title={error}
                aria-label={`${field} ${method.label} ${country.code} ${segment}`}
                data-testid={`fee-${field}-${id}`}
                $invalid={!!error}
                ref={ref}
                {...registered}
            />
        );
    };

    const renderRemove = (row: ProductRow, index: number) => (
        <RemoveCodeButton
            type="button"
            title={`Remove ${row.productCode} from ${method.label} in this market and segment`}
            aria-label={`Remove ${row.productCode} from ${method.label}`}
            data-testid={`remove-product-code-${row.productCode}-${country.code}-${segment}`}
            onClick={() => removeCode(index)}
        >
            ×
        </RemoveCodeButton>
    );

    const renderValidity = (row: ProductRow, index: number) => (
        <ValidityFields>
            {(Object.keys(validityFieldLabels) as ValidityField[]).map(field => {
                const { ref, ...registered } = register(`products.${key}.${index}.${field}`);

                return (
                    <ValidityInput
                        key={field}
                        type="date"
                        title={validityFieldLabels[field]}
                        aria-label={`${validityFieldLabels[field]} ${row.productCode}`}
                        data-testid={`${field}-${row.productCode}-${country.code}-${segment}`}
                        ref={ref}
                        {...registered}
                    />
                );
            })}
        </ValidityFields>
    );

    const { ref: activeRef, ...activeField } = register(`active.${key}`);

    return (
        <>
            <tr
                data-testid={`price-row-${id}`}
                data-active={active}
                data-changed={changed}
                title={changed ? 'Changed, not saved yet' : undefined}
            >
                <Td>
                    <PaymentMethodCodes
                        method={method}
                        rows={fields}
                        rowKey={(row, index) => fields[index]?.id ?? row.productCode}
                        renderAfterCode={renderRemove}
                        renderValidity={renderValidity}
                    />
                </Td>
                <Td
                    title={
                        fields.length === 0 ? 'Add a product code to price the method' : undefined
                    }
                >
                    <CheckBox
                        id={`active-${id}`}
                        noMargin
                        disabled={fields.length === 0}
                        dataTestId={`active-${id}`}
                        setRef={activeRef}
                        {...activeField}
                    />
                </Td>
                {feeFields.map(field => (
                    <Td key={field} $numeric>
                        {renderFee(field)}
                    </Td>
                ))}
            </tr>
        </>
    );
};

export default PaymentMethodEditRow;
